import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { surveysService } from "../services/surveys";
import { responsesService } from "../services/responses";
import { useAuthStore } from "../store/authStore";
import { useState, useMemo, useCallback } from "react";
import { FundSurveyButton } from "../components/FundSurveyButton";
import CountdownTimer from "../components/CountdownTimer";

export default function SurveyDetailPage() {
  const { id } = useParams() as { id: string };
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["survey", id],
    queryFn: () => surveysService.get(id!),
    enabled: !!id,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => surveysService.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["surveys"] });
      navigate("/surveys");
    },
    onError: (error: any) => {
      setMessage(error.message || "Failed to delete survey");
    },
  });

  // Also check if user has responded via backend API
  const { data: myResponses } = useQuery({
    queryKey: ["my-responses"],
    queryFn: () => responsesService.getMy(),
    enabled: !!user,
  });

  // Derived values
  const survey = data?.survey;
  const isOwner = survey?.is_owner;

  // Check if user has responded to this specific survey
  const hasResponded = useMemo(() => {
    if (!myResponses?.responses || !survey?.questions?.[0]?.id) return false;
    return myResponses.responses.some(
      (r: any) => r.survey_id === survey.id
    );
  }, [myResponses, survey]);

  // Check if survey deadline has passed
  const isExpired = useMemo(() => {
    if (!survey?.deadline) return false;
    return new Date(survey.deadline) < new Date();
  }, [survey?.deadline]);

  // Check if user can respond (authenticated and hasn't voted yet)
  const canRespond = useMemo(() => {
    if (!user) return false;
    if (hasResponded) return false;
    if (survey?.status !== "active" && survey?.status !== "collecting") return false;
    if (isExpired) return false;
    return true;
  }, [user, hasResponded, survey?.status, isExpired]);

  // Get user's selected option for a question from myResponses (for tick display)
  const getSelectedOptionId = useCallback((questionId: string) => {
    if (!myResponses?.responses) return null;
    const response = myResponses.responses.find((r: any) => r.question_id === questionId);
    return response?.selected_option_id || null;
  }, [myResponses]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-400">Loading survey...</div>
      </div>
    );
  }

  if (error || !survey) {
    return (
      <div className="card">
        <p className="text-red-400">
          Error loading survey: {(error as Error)?.message || "Survey not found"}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 p-6">
      {/* Page Header */}
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-100">{survey.title || "Survey"}</h1>
        <div className="flex gap-3">
          {survey.status === "draft" && (
            <>
              <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-400">
                ← Back
              </button>
              <button
                onClick={() => {
                  if (confirm("Are you sure you want to delete this draft survey?")) {
                    deleteMutation.mutate();
                  }
                }}
                disabled={deleteMutation.isPending}
                className="btn bg-red-600 hover:bg-red-700 text-white"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {/* Budget Card */}
        <div className="card">
          <h3 className="text-sm font-medium text-gray-400 mb-1">Budget</h3>
          <p className="text-xl font-bold text-primary-300">{survey.budget} points</p>
        </div>

        {/* Status Card - shows Time Remaining for active/collecting surveys */}
        <div className="card">
          <h3 className="text-sm font-medium text-gray-400 mb-1">
            {(survey.status === "active" || survey.status === "collecting") && survey.deadline ? "Time Remaining" : "Status"}
          </h3>
          {(survey.status === "active" || survey.status === "collecting") && survey.deadline ? (
            <CountdownTimer deadline={survey.deadline} className="text-xl font-bold" />
          ) : (
            <p className="text-xl font-bold capitalize text-gray-100">{survey.status}</p>
          )}
        </div>

        {/* Target Responses Card */}
        <div className="card">
          <h3 className="text-sm font-medium text-gray-400 mb-1">Target Responses</h3>
          <p className="text-xl font-bold text-gray-200">{survey.target_responses}</p>
        </div>

        {/* Actual Responses Card */}
        <div className="card">
          <h3 className="text-sm font-medium text-gray-400 mb-1">Responses</h3>
          <p className="text-xl font-bold text-green-400">{survey.actual_responses || 0}</p>
        </div>
      </div>

      {/* Funding Button for Pending Surveys */}
      {survey.status === "pending" && (
        <div className="card mb-8">
          <FundSurveyButton
            surveyId={survey.id}
            title={survey.title}
            description={survey.description || ""}
            budget={survey.budget}
            targetResponses={survey.target_responses}
            optionCount={survey.questions?.[0]?.options?.length || 5}
            responseWindowHours={survey.response_window_hours}
          />
        </div>
      )}

      {/* Questions Section */}
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-100">Questions</h2>

          {/* Respond button - shown next to Questions heading */}
          {canRespond && (
            <Link
              to={`/surveys/${id}/respond`}
              className="btn btn-primary"
            >
              Respond to Survey
            </Link>
          )}

          {/* Show sign-in prompt for non-logged-in users */}
          {!user && !hasResponded && (survey.status === "active" || survey.status === "collecting") && (
            <div className="text-sm text-gray-400">
              <Link to="/login" className="text-primary-500 hover:text-primary-400 font-medium">
                Sign in
              </Link>
              <span> to respond to this survey</span>
            </div>
          )}

          {/* Show message if user has responded */}
          {hasResponded && (
            <span className="text-sm text-green-400 italic">
              ✓ You have already responded to this survey
            </span>
          )}
        </div>

        {/* Display questions and options with ticks */}
        {survey.questions?.map((question: any, qIndex: number) => (
          <div key={question.id} className="card">
            <h3 className="text-xl font-bold text-gray-100 mb-4">
              {qIndex + 1}. {question.question_text}
            </h3>

            <div className="space-y-3">
              {question.options?.map((option: any, optIndex: number) => {
                // Get user's selected option from myResponses
                const selectedOptionId = getSelectedOptionId(question.id);
                const isSelected = selectedOptionId === option.id;

                return (
                  <div
                    key={option.id}
                    className="flex items-center justify-between p-4 border border-dark-border rounded-lg bg-dark-surface"
                  >
                    <div className="flex items-center flex-1">
                      <span className="text-gray-200 font-medium">
                        Option #{optIndex + 1}: {option.option_text}
                      </span>
                    </div>

                    {/* Show tick if user has selected this option */}
                    {isSelected && (
                      <span className="text-green-500 text-2xl font-bold ml-4">✓</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
